// src/components/well3d/Well3DPanel.jsx

import { Axis3D, Play, ArrowDown, Eye, EyeOff, X } from 'lucide-react';
import { useWell3DStore } from '../../stores/useWell3DStore';
import { useAoiStore } from '../../stores/useAoiStore';

const FORMATION_COLORS = {
  'Wolfcamp A':   '#378ADD',
  'Wolfcamp B':   '#1D9E75',
  'Wolfcamp':     '#378ADD',
  'Bone Spring':  '#EF9F27',
  'Spraberry':    '#D85A30',
  'Delaware Mtn': '#7F77DD',
  'Eagle Ford':   '#D4537E',
  'Haynesville':  '#639922',
  'Barnett':      '#BA7517',
  'Austin Chalk': '#5DCAA5',
};

function getFormationColor(name) {
  if (!name) return '#888780';
  for (const [key, color] of Object.entries(FORMATION_COLORS)) {
    if (name.toLowerCase().includes(key.toLowerCase())) return color;
  }
  return '#888780';
}

export default function Well3DPanel({ surfaceRef }) {
  const wells        = useWell3DStore(s => s.wells);
  const surfaceWells = useWell3DStore(s => s.surfaceWells);
  const loading      = useWell3DStore(s => s.loading);
  const loadMsg      = useWell3DStore(s => s.loadMsg);
  const error        = useWell3DStore(s => s.error);
  const selected     = useWell3DStore(s => s.selected);
  const setSelected  = useWell3DStore(s => s.setSelected);
  const camera       = useWell3DStore(s => s.camera);

  // Project AOI — scope shown in the panel header
  const aois          = useAoiStore(s => s.aois);
  const selectedAoiId = useAoiStore(s => s.selectedAoiId);
  const activeAoiId   = useAoiStore(s => s.activeAoiId);
  const projectAoiId  = selectedAoiId || activeAoiId;
  const projectAoi    = aois.find(a => a.id === projectAoiId);

  const selectedWell = selected ? wells.find(w => w.api === selected) : null;

  /* ---- No project AOI selected ---- */
  if (!projectAoiId) {
    return (
      <div className="h-full flex flex-col bg-bg-soft">
        <div className="p-3 border-b border-slate-800">
          <div className="text-xs font-mono uppercase tracking-wider text-gray-400 flex items-center gap-2">
            <Axis3D size={12} />
            Well3D
          </div>
        </div>
        <div className="flex-1 flex items-center justify-center p-6 text-center">
          <div className="text-[11px] text-fg-secondary leading-relaxed">
            Select a project AOI from the AOIs panel to fly through.
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col bg-bg-soft">

      {/* Header */}
      <div className="p-3 border-b border-slate-800">
        <div className="flex items-center justify-between">
          <div>
            <div className="text-xs font-mono uppercase tracking-wider text-gray-400 flex items-center gap-2">
              <Axis3D size={12} />
              Well3D
            </div>
            <div className="text-[10px] text-fg-secondary mt-0.5">
              {loading
                ? loadMsg || 'Loading…'
                : `${wells.length}/${surfaceWells.length} wells in ${projectAoi?.name ?? 'AOI'}`
              }
            </div>
          </div>
          {loading && <div className="w-2 h-2 rounded-full bg-status-busy animate-pulse" />}
        </div>
      </div>

      {/* Fly-through controls */}
      <div className="px-3 py-2 border-b border-slate-800 flex flex-col gap-1.5">
        <button
          onClick={() => surfaceRef.current?.playFlythrough()}
          disabled={loading || !wells.length}
          className="flex items-center gap-2 px-2.5 py-1.5 text-[11px] uppercase tracking-wider
                     rounded-md border border-accent-action/40 text-accent-action
                     hover:bg-accent-action/15 disabled:opacity-50 disabled:cursor-not-allowed
                     transition"
        >
          <Play size={11} />
          Play Fly-Through
        </button>

        <div className="flex gap-1.5">
          <button
            onClick={() => surfaceRef.current?.snapSubsurface()}
            disabled={loading || !wells.length}
            className="flex-1 flex items-center justify-center gap-1.5 px-2 py-1.5 text-[10px]
                       uppercase tracking-wider rounded-md border border-slate-700 bg-slate-800/40
                       text-gray-300 hover:bg-slate-800/70 disabled:opacity-50 transition"
          >
            <ArrowDown size={10} />
            Subsurface
          </button>
          <button
            onClick={() => surfaceRef.current?.toggleSurface()}
            className="flex-1 flex items-center justify-center gap-1.5 px-2 py-1.5 text-[10px]
                       uppercase tracking-wider rounded-md border border-slate-700 bg-slate-800/40
                       text-gray-300 hover:bg-slate-800/70 transition"
          >
            {camera.alpha > 0.5 ? <EyeOff size={10} /> : <Eye size={10} />}
            Surface
          </button>
        </div>
      </div>

      {/* Camera readout */}
      <div className="px-3 py-1.5 border-b border-slate-800 flex justify-between text-[9px] font-mono text-fg-secondary">
        <span>
          h: <span className={camera.h < 0 ? 'text-accent-info' : 'text-gray-400'}>
            {camera.h.toFixed(0)}m
          </span>
        </span>
        <span>pitch: {camera.pitch.toFixed(0)}°</span>
        <span>α: {camera.alpha.toFixed(2)}</span>
      </div>

      {error && (
        <div className="px-3 py-2 text-[10px] text-red-400 border-b border-slate-800">
          {error}
        </div>
      )}

      {/* Selected well */}
      {selectedWell && (
        <div className="border-b border-slate-800 bg-slate-800/30">
          <div className="flex justify-end px-3 pt-2">
            <button
              onClick={() => setSelected(null)}
              className="text-[10px] text-fg-secondary hover:text-gray-300 flex items-center gap-1"
            >
              <X size={11} /> Close
            </button>
          </div>
          <div className="px-3 pb-3">
            <div className="text-[11px] font-mono text-gray-200 mb-1">
              API {selectedWell.api}
            </div>
            <div className="text-[10px] text-gray-400 space-y-0.5">
              {selectedWell.formation && (
                <div className="flex items-center gap-1.5">
                  <span
                    className="w-2 h-2 rounded-sm shrink-0"
                    style={{ background: getFormationColor(selectedWell.formation) }}
                  />
                  {selectedWell.formation}
                </div>
              )}
              <div>TVD {(selectedWell.tvdFt / 1000).toFixed(1)}k ft</div>
              <div>{selectedWell.wellType ?? '—'}</div>
            </div>
          </div>
        </div>
      )}

      {/* Well list */}
      <div className="flex-1 overflow-y-auto">
        {!wells.length && !loading && (
          <div className="px-6 py-8 text-[10px] text-fg-secondary text-center leading-relaxed">
            No wells with directional data in this AOI.
          </div>
        )}
        {wells.map((w) => (
          <div
            key={w.api}
            onClick={() => {
              setSelected(w.api);
              surfaceRef.current?.flyToWell(w.api);
            }}
            className={`px-3 py-2 border-b border-slate-800/60 cursor-pointer transition-colors
              ${selected === w.api
                ? 'border-l-2 border-l-accent-action'
                : 'hover:bg-slate-800/40'}`}
          >
            <div className="flex items-center gap-2">
              <span
                className="w-2 h-2 rounded-full shrink-0"
                style={{ background: getFormationColor(w.formation) }}
              />
              <span className="text-[11px] font-mono text-gray-200 flex-1 truncate">
                {w.api}
              </span>
              <span className="text-[10px] text-fg-secondary font-mono shrink-0">
                {(w.tvdFt / 1000).toFixed(1)}k ft
              </span>
            </div>
            {w.formation && (
              <div className="text-[10px] text-fg-secondary mt-0.5 pl-4 truncate">
                {w.formation}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
